import React, { useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import GlobalFeedback from "../../../global/GlobalFeedback";
import GlobalSuccessFlashModal from "../../../global/GlobalSuccessFlashModal";
import GlobalQuickJump from "../../../global/GlobalQuickJump";
import { useScheduleInlineEdit } from "../utils/useScheduleInlineEdit";
import ScheduleEditPanel from "../components/form/ScheduleEditPanel";
import AdminDashboardCards from "../components/admin/AdminDashboardCards";
import ScheduleTypeMasterSection from "../components/masters/scheduleType/ScheduleTypeMasterSection";
import ScheduleStatusMasterSection from "../components/masters/scheduleStatus/ScheduleStatusMasterSection";
import PriorityTypeMasterSection from "../components/masters/priorityType/PriorityTypeMasterSection";
import ScheduleDirectorySection from "../components/directory/ScheduleDirectorySection";
import DeleteScheduleModal from "../components/modals/DeleteScheduleModal";
import { ScheduleProvider } from "../components/context/ScheduleContext";
import { useScheduleRole } from "../utils/useScheduleRole";
import { SCHEDULE_CONTROL_SUBMODULE_SLUG } from "../utils/schedulePermissionSlugs";
import { useScheduleDashboardStats } from "../utils/useScheduleDashboardStats";
import { notifyScheduleDataChanged } from "../utils/scheduleDataEvents";
import "../styles/scheduleDashboard.css";
import "../styles/scheduleTable.css";
import "../../user-builder/styles/userBuilder.css";
import "../../user-builder/styles/UserBuilderPage.css";

function ScheduleControlContent() {
  const location = useLocation();
  const legacyEditHandled = useRef(false);
  const perms = useScheduleRole(SCHEDULE_CONTROL_SUBMODULE_SLUG);
  const {
    editingSchedule,
    editError,
    openEdit,
    openEditById,
    cancelEdit,
    clearEdit,
  } = useScheduleInlineEdit("schedule-control-edit");
  const [successMsg, setSuccessMsg] = useState("");
  const [warningMsg, setWarningMsg] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [activeQuickJump, setActiveQuickJump] = useState(null);
  const [mastersVersion] = useState(0);
  const [reloadToken, setReloadToken] = useState(0);

  const { dashboardStats, loadingStats, refreshStats } = useScheduleDashboardStats({
    enabled: perms.canView,
    reloadToken,
    listScope: "all",
  });

  useEffect(() => {
    const editId = location.state?.editScheduleId;
    if (!editId || legacyEditHandled.current) return;
    legacyEditHandled.current = true;
    openEditById(editId);
    window.history.replaceState({}, document.title, location.pathname);
  }, [location.state?.editScheduleId, location.pathname, openEditById]);

  const handleSuccess = (msg) => {
    setSuccessMsg(msg);
    clearEdit();
    setReloadToken((t) => t + 1);
    notifyScheduleDataChanged();
    refreshStats({ silent: true });
  };

  const handleEditSuccess = (msg) => {
    handleSuccess(msg);
    cancelEdit();
  };

  const quickJumpItems = useMemo(() => {
    const items = [];
    if (perms.canView) {
      items.push({
        key: "stats",
        label: "Performance overview",
        targetId: "schedule-control-stats",
      });
      items.push({
        key: "list",
        label: "Schedule directory",
        targetId: "schedule-control-list",
      });
    }
    if (editingSchedule && perms.canEdit) {
      items.push({ key: "edit", label: "Edit schedule", targetId: "schedule-control-edit" });
    }
    if (perms.canManageMasters) {
      items.push({
        key: "masters",
        label: "Master table management",
        targetId: "schedule-control-masters",
      });
    }
    return items;
  }, [editingSchedule, perms.canView, perms.canEdit, perms.canManageMasters]);

  return (
    <div className="user-builder-shell">
      <header className="user-builder-hero">
        <div className="user-builder-hero__text">
          <p className="user-builder-hero__eyebrow">Administrative area</p>
          <h2 className="user-builder-hero__title">Schedules control</h2>
          <p className="user-builder-hero__sub">
            All schedule entries, directory filters, and master tables (types, statuses, priorities).
            Permissions follow schedule-control in User builder. Admin cannot create schedules here.
          </p>
        </div>
      </header>

      <GlobalSuccessFlashModal message={successMsg} onDismiss={() => setSuccessMsg("")} />
      <GlobalSuccessFlashModal
        message={warningMsg}
        onDismiss={() => setWarningMsg("")}
        title="Warning"
        tone="warning"
      />
      <GlobalFeedback type="error" text={editError} role="alert" />
      {perms.permissionsLoading ? (
        <p className="schedule-table-loading" style={{ padding: "2rem 1.5rem" }}>
          Loading permissions…
        </p>
      ) : null}
      {!perms.permissionsLoading && !perms.canView && !perms.canManageMasters ? (
        <Navigate to="/user-login" replace />
      ) : null}
      <div className="user-builder-layout">
        <div className="user-builder-layout__main">
          {!perms.permissionsLoading && perms.canView ? (
            <section
              id="schedule-control-stats"
              className="user-builder-panel"
              style={{ scrollMarginTop: "2rem" }}
            >
              <div className="user-builder-panel__head">
                <h3 className="user-builder-panel__title">Performance overview</h3>
                <p className="user-builder-panel__hint">Live statistics for all schedule entries.</p>
              </div>
              <AdminDashboardCards loading={loadingStats} dashboardStats={dashboardStats} />
            </section>
          ) : null}

          {!perms.permissionsLoading && perms.canView ? (
            <section
              id="schedule-control-list"
              className="user-builder-panel"
              style={{ scrollMarginTop: "2rem" }}
            >
              <ScheduleDirectorySection
                isAdmin
                listScope="all"
                showPanelHead
                permissions={perms}
                reloadToken={reloadToken}
                onEdit={perms.canEdit ? openEdit : undefined}
                onDelete={perms.canDelete ? setDeleteTarget : undefined}
              />
            </section>
          ) : null}

          {editingSchedule ? (
            <section
              id="schedule-control-edit"
              className="user-builder-panel"
              style={{ scrollMarginTop: "2rem" }}
            >
              <ScheduleEditPanel
                schedule={editingSchedule}
                onCancel={cancelEdit}
                onSuccess={handleEditSuccess}
              />
            </section>
          ) : null}

          {perms.canManageMasters ? (
            <section
              id="schedule-control-masters"
              className="user-builder-panel"
              style={{ scrollMarginTop: "2rem" }}
            >
              <div className="user-builder-panel__head">
                <h3 className="user-builder-panel__title">Master table management</h3>
                <p className="user-builder-panel__hint">
                  Schedule types, statuses, and priority types used on the entry form.
                </p>
              </div>
              <div
                className="admin-masters-unified"
                style={{ display: "flex", flexDirection: "column", gap: "2rem", marginTop: "1rem" }}
              >
                <div id="schedule-type-master" style={{ scrollMarginTop: "2rem" }}>
                  <h4 className="schedule-list-panel__section-title">Schedule types</h4>
                  <ScheduleTypeMasterSection key={`type-${mastersVersion}`} showInactive />
                </div>
                <div id="schedule-status-master" style={{ scrollMarginTop: "2rem" }}>
                  <h4 className="schedule-list-panel__section-title">Schedule statuses</h4>
                  <ScheduleStatusMasterSection key={`status-${mastersVersion}`} showInactive />
                </div>
                <div id="priority-type-master" style={{ scrollMarginTop: "2rem" }}>
                  <h4 className="schedule-list-panel__section-title">Priority types</h4>
                  <PriorityTypeMasterSection key={`priority-${mastersVersion}`} showInactive />
                </div>
              </div>
            </section>
          ) : null}
        </div>

        {quickJumpItems.length > 0 ? (
          <GlobalQuickJump
            asideAriaLabel="Schedules control navigation"
            items={quickJumpItems}
            activeKey={activeQuickJump}
            onActiveChange={setActiveQuickJump}
          />
        ) : null}
      </div>

      <DeleteScheduleModal
        schedule={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onSuccess={handleSuccess}
        onError={(msg) => setWarningMsg(msg)}
      />
    </div>
  );
}

/** Admin — Office Records → Schedules (`/app/schedules/control`). */
export default function ScheduleControlPage() {
  return (
    <ScheduleProvider>
      <ScheduleControlContent />
    </ScheduleProvider>
  );
}
